import { useRef, useState, useCallback } from 'react';
import { message } from 'antd';
import {
  DEFAULT_COMPRESS_THRESHOLD_BYTES,
  uploadImageWithOptionalCompression,
} from '../utils/imageUpload';

/**
 * Uploads a local image to Cloudinary (with optional size/compress prompt) then adds it to the fabric canvas.
 */
export function useCanvasImageCloudinaryUpload({
  roomId,
  createImage,
  setSavingData,
  isTemplateDocument,
  messageKey = 'canvas-image-upload',
  compressionZIndex = 2000,
}) {
  const [compressModalOpen, setCompressModalOpen] = useState(false);
  const [compressFileSize, setCompressFileSize] = useState(0);
  const compressChoiceRef = useRef(null);

  const closeCompressModal = useCallback(
    (choice) => {
      setCompressModalOpen(false);
      const pending = compressChoiceRef.current;
      compressChoiceRef.current = null;
      if (!pending) return;
      if (choice === 'cancel') {
        pending.reject(Object.assign(new Error('cancelled'), { isCancel: true }));
        return;
      }
      message.loading({
        key: messageKey,
        content:
          choice === 'compress'
            ? 'Compressing and uploading image...'
            : 'Uploading original image...',
      });
      pending.resolve(choice === 'compress');
    },
    [messageKey],
  );

  const addImageToCanvasFromFile = useCallback(
    async (file) => {
      if (!file) return;
      if (!file.type || !file.type.startsWith('image/')) {
        message.warning('Please use an image file');
        return;
      }
      try {
        if (file.size <= DEFAULT_COMPRESS_THRESHOLD_BYTES) {
          message.loading({ content: 'Uploading image...', key: messageKey });
        }

        const cloudinaryUrl = await uploadImageWithOptionalCompression(file, {
          thresholdBytes: DEFAULT_COMPRESS_THRESHOLD_BYTES,
          promptCompress: async (largeFile) => {
            message.destroy(messageKey);
            return new Promise((resolve, reject) => {
              setCompressFileSize(largeFile.size);
              compressChoiceRef.current = { resolve, reject };
              setCompressModalOpen(true);
            });
          },
        });

        createImage(roomId, cloudinaryUrl, setSavingData, isTemplateDocument);
        message.success({ content: 'Image added to canvas', key: messageKey });
      } catch (error) {
        if (error?.isCancel) return;
        console.error('Canvas image upload failed:', error);
        message.error({ content: error?.message || 'Failed to upload image', key: messageKey });
        throw error;
      }
    },
    [roomId, createImage, setSavingData, isTemplateDocument, messageKey],
  );

  return {
    addImageToCanvasFromFile,
    compressModalOpen,
    compressFileSize,
    closeCompressModal,
    compressionZIndex,
  };
}
